/**
 * useFlowLeaveGuard
 *
 * Two-layer protection against accidental navigation while a CoPilot
 * flow has unflushed work:
 *
 *   1. Wouter route changes (in-app navigation)
 *      Patches `window.history.pushState` and `window.history.replaceState`
 *      to intercept programmatic navigation, then calls the user-supplied
 *      `onAttemptedLeave` callback. The callback returns:
 *        - `true`  → allow the navigation (user confirmed)
 *        - `false` → block (user cancelled, or modal still open)
 *      Wouter listens to `popstate` + `pushState` events, so this naturally
 *      catches `setLocation('/somewhere')`, `<Link href>` clicks, etc.
 *
 *   2. Browser tab close / refresh / external nav
 *      Standard `beforeunload` handler that triggers the browser's native
 *      "Leave site?" confirm dialog. The dialog is OS-styled (we can't
 *      customize it), but it's a critical backstop because the in-app
 *      modal can't run during a tab-close.
 *
 * Both layers are wired only when `enabled === true` so the host can
 * disable the guard during the post-completion redirect, etc.
 *
 * Note on Wouter: there's no built-in `useBlocker` like React Router 6.
 * We do the history-patching ourselves, which is a known-stable pattern
 * (Wouter listens to the same events). If you read Wouter's source for
 * `useBrowserLocation`, you'll see why this works.
 */

import { useEffect, useRef } from 'react';

export interface UseFlowLeaveGuardArgs {
  /**
   * Whether the guard is currently armed. When false, both layers
   * are unhooked. Hosts typically tie this to `state.isDirty` from
   * the lifecycle context.
   */
  enabled: boolean;
  /**
   * Called when the user tries to navigate (via Wouter / Link / etc.).
   *
   * Return / resolve `true` to ALLOW the navigation through, `false`
   * to BLOCK it. Async — the manager opens its modal and awaits the
   * user's confirm/cancel choice.
   *
   * The default URL the user was trying to reach is passed so the
   * modal can show "Leaving for: /home" if useful.
   */
  onAttemptedLeave: (intendedUrl: string) => Promise<boolean>;
  /**
   * Optional message for the browser's native beforeunload dialog.
   * Modern browsers ignore this and show their own generic copy, but
   * setting `returnValue` to a non-empty string is what triggers the
   * dialog at all. Default: `'unsaved-progress'`.
   */
  beforeUnloadMessage?: string;
}

export function useFlowLeaveGuard({
  enabled,
  onAttemptedLeave,
  beforeUnloadMessage = 'unsaved-progress',
}: UseFlowLeaveGuardArgs): void {
  // We hold the LATEST callback in a ref so the patched history methods
  // always call the current closure (avoids stale-closure bugs across
  // re-renders).
  const callbackRef = useRef(onAttemptedLeave);
  useEffect(() => {
    callbackRef.current = onAttemptedLeave;
  }, [onAttemptedLeave]);

  // ── Layer 1: in-app navigation guard via history patching ────────
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;

    const originalPushState = window.history.pushState.bind(window.history);
    const originalReplaceState = window.history.replaceState.bind(window.history);

    // Track in-flight confirmation prompts so concurrent triggers don't
    // pile up modals. Once a leave is in flight, all other navigations
    // are silently blocked until the current one resolves.
    let leaveInFlight = false;

    async function intercept(
      method: typeof originalPushState,
      args: Parameters<typeof originalPushState>
    ): Promise<void> {
      const url = String(args[2] ?? '');

      if (leaveInFlight) return; // already prompting; silently drop
      leaveInFlight = true;
      try {
        let allowed = true;
        try {
          allowed = await callbackRef.current(url);
        } catch (err) {
          // The callback (`onAttemptedLeave`) lives on the FLM
          // and returns a Promise resolved by the Stay/Leave modal.
          // If it throws (FLM unmounting mid-prompt, host bug,
          // unhandled exception inside a custom resolver) we MUST
          // NOT trap the user on the current URL — fail open and
          // let the navigation through. Without this, a single
          // mistake in the callback chain would silently swallow
          // every subsequent in-app link click.
          // See radar 2026-04-28 — H-7.

          // Use console here intentionally — the leave-guard runs in
          // patched-history-method context where injecting a React
          // hook is awkward. The breadcrumb-friendly logger isn't
          // available; we log a stable tag so QA can grep it.

          console.warn('[CP:LeaveGuard] callback.threw', {
            url,
            err: err instanceof Error ? err.message : String(err),
          });
          allowed = true;
        }
        if (allowed) {
          method(...args);
        }
      } finally {
        leaveInFlight = false;
      }
    }

    window.history.pushState = function (...args) {
      // `void` is intentional: the Promise is owned by the patched
      // method; rejections are caught inside `intercept` (see
      // try/catch above) so nothing propagates here. If you ever
      // remove the inner catch, this `void` will start swallowing
      // rejections silently.
      void intercept(originalPushState, args);
    } as typeof window.history.pushState;

    window.history.replaceState = function (...args) {
      void intercept(originalReplaceState, args);
    } as typeof window.history.replaceState;

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [enabled]);

  // ── Layer 2: browser tab close / refresh ─────────────────────────
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;

    function handler(event: BeforeUnloadEvent) {
      // Modern browsers require BOTH preventDefault() and assigning
      // returnValue to a non-empty string to trigger the dialog.
      // The actual displayed text is browser-controlled.
      event.preventDefault();
      event.returnValue = beforeUnloadMessage;
      return beforeUnloadMessage;
    }

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [enabled, beforeUnloadMessage]);
}
